import { lazy, type ComponentType, type LazyExoticComponent } from "react";

// Per-tab guard so a single stale-chunk reload can't loop. sessionStorage (not
// localStorage) is the right scope: each tab that hits a stale chunk should get
// its own one-shot reload.
const RELOAD_KEY = "chunk-reload-once";

/**
 * Drop-in replacement for React.lazy that survives post-deploy stale chunks.
 *
 * Route code-splitting emits hashed chunk filenames. After a deploy the server
 * serves a new index that references new chunk names, but a browser tab still
 * running the OLD index has the old names baked in. When such a tab navigates
 * to a not-yet-loaded lazy route, the dynamic import() 404s and rejects —
 * without this wrapper that bubbles to the top-level error boundary and the
 * user sees a full-page crash until they manually reload.
 *
 * Instead we reload the page ONCE on the first import failure to pull the fresh
 * index (which has the correct chunk URLs). A sessionStorage guard prevents an
 * infinite reload loop when the failure is NOT a stale chunk (e.g. the chunk
 * genuinely errors at eval, or the network is offline): the second failure is
 * rethrown so the error boundary can render.
 *
 * The generic preserves each component's prop type, so callers can still pass
 * props/keys (e.g. `<QuestionEditPage key="create" mode="create" />`).
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      const mod = await factory();
      // Clear the guard on any successful load so a future stale chunk (after
      // the NEXT deploy) is again allowed one reload.
      sessionStorage.removeItem(RELOAD_KEY);
      return mod;
    } catch (err) {
      if (!sessionStorage.getItem(RELOAD_KEY)) {
        sessionStorage.setItem(RELOAD_KEY, "1");
        window.location.reload();
        // Never resolve — the reload is imminent, so nothing should render (and
        // the Suspense fallback stays up) until the fresh document takes over.
        return new Promise<{ default: T }>(() => {});
      }
      // Already reloaded once and still failing → genuine error. Let it bubble
      // to the nearest error boundary.
      throw err;
    }
  });
}
